/**
 * Lightweight wrapper around `posthog-js` that only loads the library in the browser.
 * Ensures server-rendered modules can depend on this helper without touching `navigator`.
 */

type PosthogModule = typeof import("posthog-js");
type PosthogClient = PosthogModule["default"];

type CaptureExceptionArgs = Parameters<PosthogClient["captureException"]>;
type CaptureArgs = Parameters<PosthogClient["capture"]>;

let posthogLoader: Promise<PosthogClient> | null = null;

function loadPosthog(): Promise<PosthogClient> | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (!posthogLoader) {
    posthogLoader = import("posthog-js").then((module) => module.default);
  }

  return posthogLoader;
}

export function capturePosthogException(
  ...args: CaptureExceptionArgs
): void {
  const loader = loadPosthog();
  if (!loader) {
    return;
  }

  loader
    .then((client) => {
      if (typeof client.captureException === "function") {
        client.captureException(...args);
      }
    })
    .catch((loaderError) => {
      console.warn("Failed to forward exception to PostHog", loaderError);
    });
}

export function capturePosthogEvent(...args: CaptureArgs): void {
  const loader = loadPosthog();
  if (!loader) {
    return;
  }

  loader
    .then((client) => {
      if (typeof client.capture === "function") {
        client.capture(...args);
      }
    })
    .catch((loaderError) => {
      console.warn("Failed to forward event to PostHog", loaderError);
    });
}
